import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from "react-router-dom";
import momenttz from '../../component/helpers/momenttz';
import { Accordion, OverlayTrigger, Tooltip } from "react-bootstrap"
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import CustomButton from '../../component/CustomButton';
import { useTranslation } from 'react-i18next';

import './ZonePanel.sass';

const ZonePanel = (props) => {
	const navigate = useNavigate();
	const addressRefs = useRef({});
	const assetRefs = useRef({});
	const { isCollapsed, setIsCollapsed, zones, map, mapUI } = props;
	const { t } = useTranslation('translation', { keyPrefix: 'homePage.zonePanel' });

	const [initZones, setInitZones] = useState(zones);
	const [mapState, setMapState] = useState({});
	const [filteredZones, setFilteredZones] = useState(zones);
	const [selectAll, setSelectAll] = useState(false);
	const [truncationAddressMap, setTruncationAddressMap] = useState({});
	const [truncationAssetMap, setTruncationAssetMap] = useState({});
	const [searchTerm, setSearchTerm] = useState('');
	const storedMap = JSON.parse(localStorage.getItem("storedMap"));
	const [isLocalData, setIsLocalData] = useState(!!storedMap?.checkedZones?.length);

	useEffect(() => {
		const timeout = setTimeout(() => {
			const newAddressMap = {};
			const newAssetMap = {};
			filteredZones.forEach(zone => {
				const elAddress = addressRefs.current[zone.id];
				const elAsset = assetRefs.current[zone.id];
				if (elAddress && zone.open) {
					newAddressMap[zone.id] = elAddress.scrollWidth > elAddress.clientWidth;
				}
				if (elAsset && zone.open) {
					newAssetMap[zone.id] = elAsset.scrollWidth > elAsset.clientWidth;
				}
			});
			setTruncationAddressMap(newAddressMap);
			setTruncationAssetMap(newAssetMap);
		}, 50);

		return () => clearTimeout(timeout);
	}, [filteredZones.map(z => z.open).join(',')]);

	useEffect(() => {
		if (map && mapUI) {
			mapUI.getControl('zoom').setAlignment('left-bottom');
			map.addEventListener('mapviewchange', handleMapViewChange);
		}

		return () => {
			if (map && mapUI) {
				mapUI.getControl('zoom').setAlignment('right-top');
				map.removeObjects(map.getObjects().filter(e => e && e instanceof H.map.Group));
			}
		}
	}, []);

	const handleMapViewChange = (e) => {
		if (e.newValue?.lookAt) {
			const { position, zoom } = e.newValue.lookAt;
			const lat = Math.trunc(position.lat * 1e7) / 1e7;
			const lng = Math.trunc(position.lng * 1e7) / 1e7;
			const z = Math.trunc(zoom * 1e2) / 1e2;
			setMapState({ lat, lng, zoom: z });
		}
	};

	useEffect(() => {
		if (mapState.zoom >= 10) {
			document.querySelectorAll('.markerLabel').forEach(label => ((label.style.visibility === 'hidden') && (label.style.visibility = 'visible')));
		} else {
			document.querySelectorAll('.markerLabel').forEach(label => ((label.style.visibility === 'visible') && (label.style.visibility = 'hidden')));
		}
	}, [mapState]);

	useEffect(() => {
		if (filteredZones.length && filteredZones.every(zone => zone.checked)) {
			setSelectAll(true);
		} else {
			setSelectAll(false);
		}

		if (map) {
			let allGroupsObject = map.getObjects().filter(e => e && e instanceof H.map.Group && e.getRemoteId() === 'allGroupsObject');
			if (allGroupsObject.length) {
				allGroupsObject = allGroupsObject[0];
			}
			else {
				allGroupsObject = new H.map.Group();
				allGroupsObject.setRemoteId('allGroupsObject');
			}

			filteredZones.map(zone => {
				let group = new H.map.Group();
				allGroupsObject.getObjects().map(e => {
					if (e.getRemoteId() === zone.id) {
						group = e;
						if (!zone.checked) {
							allGroupsObject.removeObject(group)
						}
					}
				});

				if (zone.checked && !allGroupsObject.getObjects().find(e => (e.getRemoteId() === zone.id))) {
					const div = document.createElement('div');
					const img = document.createElement('img');
					const label = document.createElement('label');
					div.setAttribute('class', 'markerZone');
					label.setAttribute('class', 'markerLabel');
					img.setAttribute('src', '/images/markerZone.svg');

					const k = (v) => (Math.round(v / 2 + zone.radius * v / 10));
					label.style.fontSize = `${k(18)}px`;
					img.style.width = `${k(38)}px`;
					img.style.height = `${k(55)}px`;
					label.style.visibility = mapState.zoom >= 10 ? 'visible' : 'hidden';
					label.innerText = zone.zone_name;
					div.appendChild(img);
					div.appendChild(label);

					const position = { lat: zone.lat, lng: zone.lon };

					const marker = new H.map.DomMarker(position, {
						icon: new H.map.DomIcon(div)
					});
					const circle = new H.map.Circle(position, (zone.radius * 1609.34), {
						style: { lineWidth: 0, fillColor: "rgba(241, 4, 4, 0.25)" }
					});
					const circleOutline = new H.map.Polyline(circle.getGeometry().getExterior(), {
						style: { lineWidth: 20, strokeColor: "rgba(255, 215, 0, 0.25)" }
					});
					circleOutline.getGeometry().pushPoint(circleOutline.getGeometry().extractPoint(0));

					group.addObjects([marker, circle, circleOutline]);
					group.setRemoteId(zone.id);
					group.addEventListener('tap', () => changeStyle(circleOutline));
					changeStyle(circleOutline);
					allGroupsObject.addObject(group);
					map.addObject(allGroupsObject);
				}
			});

			setTimeout(() => {
				map.getViewPort().setPadding(15, (window.innerWidth > 568 ? 250 : 182), 15, 15);
				initZones.some(zone => zone.checked) && map.getViewModel().setLookAtData({ bounds: allGroupsObject?.getBoundingBox() });
			}, isLocalData ? 1000 : 0);
			isLocalData && setIsLocalData(false);

			const listZones = initZones.filter(zone => zone.checked);
			if (listZones.length === 1) {
				let zoom;
				const r = listZones[0].radius;
				if (r >= 0.01 && r <= 0.5) {
					zoom = 15
				} else if (r > 0.5 && r <= 1) {
					zoom = 14
				} else if (r > 1 && r <= 2) {
					zoom = 13
				} else if (r > 2 && r <= 5) {
					zoom = 12
				}
				map.setZoom(zoom);
			}
		}

		localStorage.setItem("storedMap", JSON.stringify({
			...storedMap,
			checkedZones: filteredZones.filter(zone => zone.checked).map(zone => ({
				id: zone.id,
				checked: zone.checked,
				open: zone.open,
				viewAll: zone.viewAll
			}))
		}));
	}, [filteredZones]);

	const changeStyle = (circleOutline) => {
		circleOutline.setVisibility(true);

		circleOutline.setStyle({
			lineWidth: 20,
			strokeColor: "rgba(255, 215, 0, 0.25)"
		});

		setTimeout(() => {
			setTimeout(() => {
				fadeOutCircle(circleOutline, 2000);
			}, 3000);
		}, 50);
	};

	const fadeOutCircle = (circleOutline, duration = 2000) => {
		const start = performance.now();
		const initialAlpha = 0.25;

		const animate = (time) => {
			const elapsed = time - start;
			const progress = Math.min(elapsed / duration, 1);
			const newAlpha = initialAlpha * (1 - progress);

			circleOutline.setStyle({
				lineWidth: 20,
				strokeColor: `rgba(255, 215, 0, ${newAlpha})`
			});

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				circleOutline.setVisibility(false);
			}
		};

		requestAnimationFrame(animate);
	};

	const addNewZone = () => {
		navigate('/zones');
	};

	const onSearch = (e) => {
		const searchTerm = e.target.value.toLowerCase();
		setSearchTerm(searchTerm);
		if (searchTerm) {
			const filteredZones = initZones.filter(zone => zone.zone_name.toLowerCase().includes(searchTerm));
			setFilteredZones(filteredZones);
		} else {
			setFilteredZones(initZones);
		}
	};

	const toggleZonesList = (zoneId) => {
		setFilteredZones(filteredZones.map(zone => (zone.id === zoneId ? { ...zone, checked: !zone.checked } : zone)));
		setInitZones(initZones.map(zone => (zone.id === zoneId ? { ...zone, checked: !zone.checked } : zone)));
	};

	const toggleSelectAll = (e) => {
		const value = e.target.checked;
		setSelectAll(value);
		const selectedFilteredZones = filteredZones.map(zone => ({ ...zone, checked: value }));
		setFilteredZones(selectedFilteredZones);
		const zoneIds = selectedFilteredZones.map(zone => zone = zone.id);
		const zones = initZones.map(zone => (zoneIds.includes(zone.id) ? { ...zone, checked: value } : zone));
		setInitZones(zones);
	};

	const showZoneBlock = (zoneId, open) => {
		const updatedZones = filteredZones.map(zone => (zone.id === zoneId ? { ...zone, open } : zone));
		setFilteredZones(updatedZones);
	};

	const viewAllZones = (zoneId) => {
		const updatedZones = filteredZones.map(zone => (zone.id === zoneId ? { ...zone, viewAll: !zone?.viewAll } : zone));
		setFilteredZones(updatedZones);
	};

	return (
		<div className="zonesPanel-container" style={{ zIndex: isCollapsed ? 1 : 8, height: isCollapsed ? '24px' : 'calc(100% - 24px)' }}>
			<div className="header">
				<svg width="13" height="12" viewBox="0 0 13 13" onClick={() => setIsCollapsed(!isCollapsed)} fill="none" transform={`rotate(${isCollapsed ? '-180' : '0'})`} xmlns="http://www.w3.org/2000/svg">
					<path d="M11.4725 12.875L13 11.5935L6.5 6.14052L0 11.5935L1.5275 12.875L6.5 8.71253L11.4725 12.875ZM11.4725 7.42198L13 6.14052L6.5 0.6875L0 6.14052L1.5275 7.42198L6.5 3.25951L11.4725 7.42198Z" fill="#0071B6" />
				</svg>
				<span>{t('titleZoneActivity')}</span>
			</div>
			{!isCollapsed && (
				<div className="content">
					{zones.length ?
						<div className="zones">
							<div className="zones-search-block">
								<input type='search' name="search" placeholder={t('placeholderSearchZones')} className="zones-search" value={searchTerm} onChange={onSearch} />
								<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M11.375 5.6875C11.375 6.94258 10.9676 8.10195 10.2812 9.04258L13.743 12.507C14.0848 12.8488 14.0848 13.4039 13.743 13.7457C13.4012 14.0875 12.8461 14.0875 12.5043 13.7457L9.04258 10.2812C8.10195 10.9703 6.94258 11.375 5.6875 11.375C2.5457 11.375 0 8.8293 0 5.6875C0 2.5457 2.5457 0 5.6875 0C8.8293 0 11.375 2.5457 11.375 5.6875ZM5.6875 9.625C6.20458 9.625 6.7166 9.52315 7.19432 9.32528C7.67204 9.1274 8.1061 8.83736 8.47173 8.47173C8.83736 8.1061 9.1274 7.67204 9.32528 7.19432C9.52315 6.7166 9.625 6.20458 9.625 5.6875C9.625 5.17042 9.52315 4.6584 9.32528 4.18068C9.1274 3.70296 8.83736 3.2689 8.47173 2.90327C8.1061 2.53764 7.67204 2.2476 7.19432 2.04972C6.7166 1.85185 6.20458 1.75 5.6875 1.75C5.17042 1.75 4.6584 1.85185 4.18068 2.04972C3.70296 2.2476 3.2689 2.53764 2.90327 2.90327C2.53764 3.2689 2.2476 3.70296 2.04972 4.18068C1.85185 4.6584 1.75 5.17042 1.75 5.6875C1.75 6.20458 1.85185 6.7166 2.04972 7.19432C2.2476 7.67204 2.53764 8.1061 2.90327 8.47173C3.2689 8.83736 3.70296 9.1274 4.18068 9.32528C4.6584 9.52315 5.17042 9.625 5.6875 9.625Z" fill="#9CA3AF" />
								</svg>
							</div>
							<div className="zones-list">
								<div className="allZzones-block">
									<label>
										<input
											type="checkbox"
											className="allZones-checkbox"
											name="zone-checkbox"
											checked={selectAll}
											onChange={toggleSelectAll}
										/>
										<span>{t('titleSelectAll') + ` (${filteredZones.length})`}</span>
									</label>
								</div>
								{filteredZones.map(zone => {
									const isAddressTruncated = truncationAddressMap[zone.id];
									const addressContent = (
										<div
											ref={el => (addressRefs.current[zone.id] = el)}
											className="collapsedContent-header"
											style={{
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis'
											}}
										>
											{zone.address}
										</div>
									);

									return (
										<div className="zone-wrapper" key={zone.id}>
											<div className="zone-block">
												<label>
													<input
														type="checkbox"
														className="zone-checkbox"
														name="zone-checkbox"
														checked={zone?.checked || false}
														onChange={() => toggleZonesList(zone.id)}
													/>
													<span>{zone.zone_name}</span>
												</label>
												{(zone?.open || false) ? <FaChevronDown className="icon-up"
													onClick={() => showZoneBlock(zone.id, false)} /> :
													<FaChevronRight className="icon-down"
														onClick={() => showZoneBlock(zone.id, true)} />}
											</div>
											<Accordion.Collapse className="collapsed-panel" in={zone?.open || false}>
												<div className="collapsedBlock">
													<div className="collapsedContent">
														{zone.movement.length ? (
															isAddressTruncated ? (
																<OverlayTrigger
																	placement="top"
																	overlay={<Tooltip
																		id={`tooltip-${zone.id}`}>{zone.address}</Tooltip>}
																>
																	{addressContent}
																</OverlayTrigger>
															) : addressContent
														) : (
															<div className="collapsedContent-header">
																{zone.address}
															</div>
														)}
														<div className="collapsedContent-item">
															<span className="collapsedContent-title">
																{zone.movement.length ? t('titleRecentActivity') : t('titleNoRecentActivity')}
															</span>
															{(zone.movement.length > 3) &&
																(<CustomButton
																	variant="link"
																	onClick={() => viewAllZones(zone.id)}
																>
																	{t('btnViewAll') + (zone?.viewAll ? t('latest') : t('all'))}
																</CustomButton>)}
														</div>
														{zone.movement.map((vehicle, index) => {
															const isAssetTruncated = truncationAssetMap[zone.id];
															const assetContent = (
																<span
																	ref={el => (assetRefs.current[zone.id] = el)}
																	className="collapsedContent-name"
																	style={{
																		whiteSpace: 'nowrap',
																		overflow: 'hidden',
																		textOverflow: 'ellipsis'
																	}}
																>
																	{vehicle.vehicle_name}
																</span>
															)
															if (index <= (zone?.viewAll ? 9 : 2)) {
																return (
																	<div key={index} className="collapsedContent-item">
																		{
																			isAssetTruncated ? (
																				<OverlayTrigger
																					placement="top"
																					overlay={<Tooltip
																						id={`tooltip-vehicle-${vehicle.asset_id}`}>{vehicle.vehicle_name}</Tooltip>}
																				>
																					{assetContent}
																				</OverlayTrigger>
																			) : assetContent
																		}
																		<div className="flexBlock">
																			<span className="collapsedContent-type"
																				style={{
																					background: vehicle.type === "entry" ? '#DCFCE7' : '#FEE2E2',
																					color: vehicle.type === "entry" ? '#166534' : '#991B1B'
																				}}>{vehicle.type === "entry" ? t('entered') : t('exited')}</span>
																			<span
																				className="collapsedContent-ts">{momenttz(`${vehicle.timestamp}+00`).format('MM/DD hh:mm A')}</span>
																		</div>
																	</div>
																)
															}
														})}
													</div>
												</div>
											</Accordion.Collapse>
										</div>
									)
								})}
							</div>
						</div>
						: <div className="no-zones-container">
							<div className="no-zones">
								<div className="no-zones-svg-round">
									<svg width="34" height="31" viewBox="0 0 34 31" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M23.9062 7.78125C23.9062 10.9805 19.623 16.6816 17.7422 19.0312C17.291 19.5938 16.4531 19.5938 16.0078 19.0312C14.127 16.6816 9.84375 10.9805 9.84375 7.78125C9.84375 3.89648 12.9902 0.75 16.875 0.75C20.7598 0.75 23.9062 3.89648 23.9062 7.78125ZM24.375 12.4922C24.5801 12.0879 24.7676 11.6836 24.9375 11.2852C24.9668 11.2148 24.9961 11.1387 25.0254 11.0684L31.8223 8.34961C32.748 7.98047 33.75 8.66016 33.75 9.65625V25.5234C33.75 26.0977 33.3984 26.6133 32.8652 26.8301L24.375 30.2227V12.4922ZM8.0625 8.85352C8.20312 9.67969 8.48438 10.5117 8.8125 11.2852C8.98242 11.6836 9.16992 12.0879 9.375 12.4922V27.2227L1.92773 30.2051C1.00195 30.5742 0 29.8945 0 28.8984V13.0312C0 12.457 0.351562 11.9414 0.884766 11.7246L8.06836 8.85352H8.0625ZM19.207 20.2031C20.0215 19.1836 21.2988 17.5254 22.5 15.6914V30.2988L11.25 27.082V15.6914C12.4512 17.5254 13.7285 19.1836 14.543 20.2031C15.7441 21.7031 18.0059 21.7031 19.207 20.2031ZM16.875 9.65625C17.4966 9.65625 18.0927 9.40932 18.5323 8.96978C18.9718 8.53024 19.2188 7.9341 19.2188 7.3125C19.2188 6.6909 18.9718 6.09476 18.5323 5.65522C18.0927 5.21568 17.4966 4.96875 16.875 4.96875C16.2534 4.96875 15.6573 5.21568 15.2177 5.65522C14.7782 6.09476 14.5312 6.6909 14.5312 7.3125C14.5312 7.9341 14.7782 8.53024 15.2177 8.96978C15.6573 9.40932 16.2534 9.65625 16.875 9.65625Z" fill="#9CA3AF" />
									</svg>
								</div>
								<h5>{t('titleNoZones')}</h5>
								<span dangerouslySetInnerHTML={{ __html: t('descriptionNoZones') }} />
								<CustomButton
									variant="primary"
									size="sm"
									onClick={addNewZone}
								>
									<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M7 0.9375C7 0.453516 6.60898 0.0625 6.125 0.0625C5.64102 0.0625 5.25 0.453516 5.25 0.9375V4.875H1.3125C0.828516 4.875 0.4375 5.26602 0.4375 5.75C0.4375 6.23398 0.828516 6.625 1.3125 6.625H5.25V10.5625C5.25 11.0465 5.64102 11.4375 6.125 11.4375C6.60898 11.4375 7 11.0465 7 10.5625V6.625H10.9375C11.4215 6.625 11.8125 6.23398 11.8125 5.75C11.8125 5.26602 11.4215 4.875 10.9375 4.875H7V0.9375Z" fill="white" />
									</svg>
									{t('btnCreateZone')}
								</CustomButton>
							</div>
						</div>}
				</div>)}
		</div >)
}

export default memo(ZonePanel);
